import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getInvocaTrackingNumber,
  isInvocaSubscribable,
  reswapInvocaNumbers,
  subscribeToInvocaComplete,
} from '../helpers/invoca';
import { clearInvocaTfn, setInvocaTfn } from '../store/action/OrderAction';
import { getAssignedPhoneNumber, setAssignedPhoneNumber } from '../helpers/assignedPhoneNumbers';

export const INVOCA_READY_POLL_MS = 500;
export const INVOCA_READY_MAX_TICKS = 120; // 60s, comfortably past any GTM load

/**
 * Resolve the phone number to display for a given base (untracked) number.
 *
 * Derived at read time from `PNAPI.placements()` - Invoca's own record of what
 * it assigned - rather than synced from a DOM mutation. That removes the
 * ordering dependency between React and Invoca: whenever a render happens the
 * current assignment is read fresh, and `onComplete` triggers a re-render when
 * a new one arrives.
 *
 * @param {string} baseTfn untracked number for the current address, or falsy
 *        when no number is rendered. Invoca assigns a tracked number per BASE
 *        number for the whole session (confirmed in prod: the same base gets the
 *        same tracked number regardless of which address put it on screen), so a
 *        number remembered here for this base is restored even after a later
 *        reswap (ELNKWEB-3321's `reswapInvocaNumbers` is global and wipes every
 *        other base's live placement) has made `PNAPI.placements()` forget it -
 *        which, since the ELNKWEB-3321 GTM fix also stops Invoca re-running on
 *        in-checkout navigation, it would otherwise never recover on its own.
 * @returns {string} tracked number when Invoca has assigned one, else baseTfn.
 */
const useTrackedTfn = (baseTfn) => {
  const dispatch = useDispatch();
  const reduxInvocaTfn = useSelector((state) => state.OrderState.invocaTfn);
  // Bases already attempted this session. Not every number getCurrentTfn can
  // return is registered with Invoca (customTFN, the testmyspeed numbers and the
  // mobile sales fallback are not), and an unregistered base can never acquire a
  // placement - without this guard every `loading` toggle would re-attempt and
  // re-lease the pool numbers of any registered numbers on the page.
  // Note: a reswap re-leases every placement on the page, while this guard only
  // knows about this hook's own base. Safe while header and CTAs share one base
  // per address, which they do today.
  const attemptedBases = useRef(new Set());
  const [invocaReady, setInvocaReady] = useState(isInvocaSubscribable);

  // Invoca is injected by GTM, so it is not ordered against React mount. When it
  // arrives late the mount-time subscription attaches to nothing and, because
  // `subscribe` is stable, React never retries - the assigned number then never
  // reaches the page until some unrelated re-render happens to re-read the
  // snapshot. Poll until its settings object exists, then flip once.
  useEffect(() => {
    if (invocaReady) {
      return undefined;
    }
    let ticks = 0;
    const timer = setInterval(() => {
      ticks += 1;
      if (isInvocaSubscribable()) {
        clearInterval(timer);
        setInvocaReady(true);
      } else if (ticks >= INVOCA_READY_MAX_TICKS) {
        // Invoca absent (lower environment, ad blocker) or far slower than any
        // realistic GTM window. Stop polling; render-time reads still self-heal.
        clearInterval(timer);
      }
    }, INVOCA_READY_POLL_MS);
    return () => clearInterval(timer);
  }, [invocaReady]);

  const subscribe = useCallback(
    (notify) => subscribeToInvocaComplete(notify),
    // Resubscribe key: flips false -> true at most once per session, when Invoca
    // loads after mount. The subscription being discarded is the no-op one, so
    // the churn is harmless.
    [invocaReady],
  );

  const getSnapshot = useCallback(() => {
    if (!baseTfn) {
      // Never look up without a base: an unfiltered lookup returns whichever
      // placement happens to be first, which belongs to a different number.
      return '';
    }
    // Live Invoca data wins whenever it is available. The remembered number is
    // only a fallback for when a reswap for a different base has made Invoca
    // forget this one's placement - never a value that should keep shadowing a
    // newer live assignment.
    return getInvocaTrackingNumber(baseTfn) || getAssignedPhoneNumber(baseTfn) || baseTfn;
  }, [baseTfn]);

  const trackedTfn = useSyncExternalStore(subscribe, getSnapshot);

  // The tracked number, or '' when Invoca has not assigned one for this base.
  const derivedTracked = trackedTfn && trackedTfn !== baseTfn ? trackedTfn : '';

  // Acquire an assignment when this base does not have one, at most once per
  // base. Invoca's own autoRun covers the common case, so this rarely fires.
  useEffect(() => {
    if (!baseTfn || getInvocaTrackingNumber(baseTfn)) {
      return;
    }
    if (attemptedBases.current.has(baseTfn)) {
      return;
    }
    // Already have this base's number remembered from earlier in the session
    // (a reswap for a different base wiped the live placement) - no need to
    // re-lease a placement for it.
    if (getAssignedPhoneNumber(baseTfn)) {
      return;
    }
    // Only record the attempt when the swap actually ran. A `false` return means
    // Invoca had not loaded yet (it arrives via GTM, unordered against mount) -
    // burning the single attempt there would leave the base untracked for the
    // session. This still cannot loop: once Invoca is available the call returns
    // true, so an unregistered base is attempted exactly once.
    if (reswapInvocaNumbers()) {
      attemptedBases.current.add(baseTfn);
    }
    // Deliberately NOT keyed on `invocaReady`. Its settings object appears
    // before the lease round trip finishes, so re-firing here would run
    // resetRun()+run() against an in-flight autoRun and re-lease the number.
    // Nothing needs it: a base rendered before a late tag is covered by
    // autoRun's own pass, and a base set afterwards re-fires on `baseTfn`.
  }, [baseTfn]);

  // Remember this base's number for the rest of the session (ELNKWEB-3321), so a
  // later reswap for a different base - which wipes Invoca's own record of this
  // one - does not strand this base on the untracked number. Keyed on the base
  // alone: Invoca hands out the same tracked number for a base regardless of
  // which address put it on screen, so this also covers a different address
  // landing on an already-attempted base, which would otherwise find neither a
  // live placement nor a reason to reswap. Kept in sync on every live read, not
  // just the first: getSnapshot already prefers live data, so this never
  // overwrites a live value with a stale one.
  useEffect(() => {
    if (derivedTracked) {
      setAssignedPhoneNumber(baseTfn, derivedTracked);
    }
  }, [baseTfn, derivedTracked]);

  // Mirror into redux for the CTAs that still read `invocaTfn`. Compared against
  // the live store value rather than a local ref, because several reducers clear
  // `invocaTfn` behind this hook's back (SET_TFN, SESSION_CALL_FULFILLED) - a ref
  // would latch and leave those consumers on the untracked number for the rest
  // of the session.
  useEffect(() => {
    if (!baseTfn) {
      // Header hidden: leave redux alone rather than clearing and restoring it.
      return;
    }
    if (derivedTracked === reduxInvocaTfn) {
      return;
    }
    if (derivedTracked) {
      dispatch(setInvocaTfn(derivedTracked));
    } else if (reduxInvocaTfn) {
      // No assignment for the current base - drop a value belonging to a
      // previous one rather than let the CTAs show another base's number.
      dispatch(clearInvocaTfn());
    }
  }, [baseTfn, derivedTracked, reduxInvocaTfn, dispatch]);

  return trackedTfn;
};

export default useTrackedTfn;
